import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Save, Trash2, Plane, Wallet, CalendarClock, MapPin, Globe2,
  Mail, Phone, Languages, Cake, Utensils, Heart, IdCard, Tag, UserCog,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { FloatingSaveButton } from "@/components/FloatingSaveButton";
import { eur, fmtDate } from "@/pages/clientes/ClientesPage";

const Stat = ({ icon: Icon, label, value }) => (
  <Card><CardContent className="flex items-center gap-3 p-4">
    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent text-accent-foreground"><Icon className="h-5 w-5" /></div>
    <div><p className="text-xs text-muted-foreground">{label}</p><p className="text-lg font-bold">{value}</p></div>
  </CardContent></Card>
);

const Field = ({ icon: Icon, label, children }) => (
  <div className="space-y-1.5">
    <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</Label>
    {children}
  </div>
);

const STATUS_LABEL = { abierto: "Abierto", cotizaciones: "Cotizaciones", cerrado: "Cerrado", terminado: "Terminado", archivado: "Archivado", cancelado: "Cancelado", viaje_empresa: "Viaje empresa" };
const COT_STATUS = { borrador: "Borrador", enviado_cliente: "Enviada", ganado: "Ganada", perdido: "Perdida", archivado: "Archivada" };

export default function ClienteDetail({ cid, onBack }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);

  const detailQ = useQuery({
    queryKey: ["cliente", cid],
    queryFn: async () => (await api.get(`/clientes/${cid}`)).data,
  });

  useEffect(() => {
    if (detailQ.data) {
      const d = detailQ.data;
      setForm({
        full_name: d.full_name || "", email: d.email || "", phone: d.phone || "", country: d.country || "",
        nationality: d.nationality || "", language: d.language || "", birthday: d.birthday || "",
        dietary_preferences: d.dietary_preferences || "", interests: d.interests || "",
        document_number: d.document_number || "", status: d.status || "prospect",
        assigned_specialist: d.assigned_specialist || "", partner: d.partner || "",
        notes: d.notes || "", marketing_opt_in: d.marketing_opt_in !== false,
      });
    }
  }, [detailQ.data]);

  const saveMut = useMutation({
    mutationFn: () => api.put(`/clientes/${cid}`, form),
    onSuccess: () => { toast.success("Ficha guardada"); qc.invalidateQueries({ queryKey: ["cliente", cid] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: () => api.delete(`/clientes/${cid}`),
    onSuccess: () => { toast.success("Cliente eliminado"); onBack(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  if (detailQ.isLoading || !form) {
    return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-64 w-full" /></div>;
  }

  const d = detailQ.data;
  const s = d.summary || {};

  return (
    <div className="space-y-5 pb-24" data-testid="cliente-detail">
      <FloatingSaveButton
        onClick={() => saveMut.mutate()}
        saving={saveMut.isPending}
        testid="cliente-save-btn"
      />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="cliente-back"><ArrowLeft className="h-5 w-5" /></Button>
          <div>
            <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight" data-testid="cliente-detail-name">
              {form.full_name}
              {form.status === "cliente"
                ? <Badge className="bg-emerald-600 hover:bg-emerald-600">Cliente</Badge>
                : <Badge variant="secondary">Prospect</Badge>}
            </h1>
            <p className="text-sm text-muted-foreground">{d.kind === "acompanante" ? "Acompañante" : "Titular"} · {form.email || "sin email"}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="outline" className="text-destructive" data-testid="cliente-delete-btn"><Trash2 className="mr-1.5 h-4 w-4" />Eliminar</Button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>¿Eliminar este cliente?</AlertDialogTitle><AlertDialogDescription>Se borrará la ficha (los viajes y cotizaciones no se eliminan).</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground" onClick={() => delMut.mutate()} data-testid="cliente-delete-confirm">Eliminar</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat icon={Plane} label="Viajes" value={s.num_trips || 0} />
        <Stat icon={Wallet} label="Gasto total" value={eur(s.total_spend)} />
        <Stat icon={CalendarClock} label="Último viaje" value={fmtDate(s.last_trip_date)} />
        <Stat icon={Globe2} label="Países visitados" value={(d.visited_countries || []).length} />
      </div>

      {(d.visited_countries?.length > 0 || d.visited_cities?.length > 0) && (
        <Card><CardContent className="space-y-3 p-4">
          {d.visited_countries?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="cliente-visited-countries">
              <span className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><Globe2 className="h-4 w-4" />Países:</span>
              {d.visited_countries.map((c) => <Badge key={c} variant="outline">{c}</Badge>)}
            </div>
          )}
          {d.visited_cities?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="cliente-visited-cities">
              <span className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><MapPin className="h-4 w-4" />Ciudades:</span>
              {d.visited_cities.map((c) => <Badge key={c} variant="secondary">{c}</Badge>)}
            </div>
          )}
        </CardContent></Card>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
        <Card className="lg:col-span-2"><CardContent className="space-y-4 p-5">
          <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Datos del cliente</h3>
          <Field icon={UserCog} label="Nombre completo"><Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="cliente-edit-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field icon={Mail} label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="cliente-edit-email" /></Field>
            <Field icon={Phone} label="Teléfono"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field icon={MapPin} label="País"><Input value={form.country} onChange={(e) => set("country", e.target.value)} /></Field>
            <Field icon={Globe2} label="Nacionalidad"><Input value={form.nationality} onChange={(e) => set("nationality", e.target.value)} /></Field>
            <Field icon={Languages} label="Idioma"><Input value={form.language} onChange={(e) => set("language", e.target.value)} placeholder="EN / ES" /></Field>
            <Field icon={Cake} label="Cumpleaños"><Input type="date" value={form.birthday || ""} onChange={(e) => set("birthday", e.target.value)} /></Field>
          </div>
          <Field icon={Utensils} label="Preferencias dietéticas"><Input value={form.dietary_preferences} onChange={(e) => set("dietary_preferences", e.target.value)} /></Field>
          <Field icon={Heart} label="Intereses"><Input value={form.interests} onChange={(e) => set("interests", e.target.value)} placeholder="gastronomía, arte, senderismo…" /></Field>
          <Field icon={IdCard} label="Documento / Pasaporte"><Input value={form.document_number} onChange={(e) => set("document_number", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field icon={UserCog} label="Travel Specialist"><Input value={form.assigned_specialist} onChange={(e) => set("assigned_specialist", e.target.value)} /></Field>
            <Field label="Estado">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="cliente-edit-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="prospect">Prospect</SelectItem><SelectItem value="cliente">Cliente</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>
          <Field icon={Tag} label="Notas"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} /></Field>
          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <Label className="flex items-center gap-1.5 text-sm"><Mail className="h-4 w-4" />Acepta marketing / mailings</Label>
            <Switch checked={form.marketing_opt_in} onCheckedChange={(v) => set("marketing_opt_in", v)} data-testid="cliente-edit-marketing" />
          </div>
        </CardContent></Card>

        <Card className="lg:col-span-3"><CardContent className="p-5">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-muted-foreground">Histórico</h3>
          <Tabs defaultValue="trips">
            <TabsList data-testid="cliente-history-tabs">
              <TabsTrigger value="trips" data-testid="tab-cliente-trips">Viajes ({s.num_trips || 0})</TabsTrigger>
              <TabsTrigger value="cots" data-testid="tab-cliente-cots">Cotizaciones ({s.num_cotizaciones || 0})</TabsTrigger>
              <TabsTrigger value="reservas" data-testid="tab-cliente-reservas">Reservas ({s.num_reservas || 0})</TabsTrigger>
            </TabsList>

            <TabsContent value="trips" className="mt-3">
              {(d.trips || []).length === 0 ? <p className="p-6 text-center text-sm text-muted-foreground">Sin viajes asociados.</p> : (
                <Table><TableHeader><TableRow><TableHead>Viaje</TableHead><TableHead>Destino</TableHead><TableHead>Fechas</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
                  <TableBody>{d.trips.map((t) => (
                    <TableRow key={t.id} data-testid={`cliente-trip-${t.id}`}>
                      <TableCell className="font-medium">{t.main_traveler}</TableCell>
                      <TableCell>{t.destination || "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{fmtDate(t.start_date)}</TableCell>
                      <TableCell><Badge variant="outline">{STATUS_LABEL[t.status] || t.status}</Badge></TableCell>
                    </TableRow>))}</TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="cots" className="mt-3">
              {(d.cotizaciones || []).length === 0 ? <p className="p-6 text-center text-sm text-muted-foreground">Sin cotizaciones asociadas.</p> : (
                <Table><TableHeader><TableRow><TableHead>Cotización</TableHead><TableHead>Destino</TableHead><TableHead className="text-right">PVP</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
                  <TableBody>{d.cotizaciones.map((c) => (
                    <TableRow key={c.id} data-testid={`cliente-cot-${c.id}`}>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell>{c.destination || "—"}</TableCell>
                      <TableCell className="text-right">{eur(c.pvp)}</TableCell>
                      <TableCell><Badge variant="outline">{COT_STATUS[c.status] || c.status}</Badge></TableCell>
                    </TableRow>))}</TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="reservas" className="mt-3">
              {(d.reservas || []).length === 0 ? <p className="p-6 text-center text-sm text-muted-foreground">Sin reservas asociadas.</p> : (
                <Table><TableHeader><TableRow><TableHead>Servicio</TableHead><TableHead>Ciudad</TableHead><TableHead>Fecha</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
                  <TableBody>{d.reservas.map((r) => (
                    <TableRow key={r.id} data-testid={`cliente-reserva-${r.id}`}>
                      <TableCell className="font-medium">{r.service}</TableCell>
                      <TableCell>{r.city || "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{fmtDate(r.date_entry)}</TableCell>
                      <TableCell><Badge variant="outline">{r.status_reserva}</Badge></TableCell>
                    </TableRow>))}</TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
        </CardContent></Card>
      </div>
    </div>
  );
}
